import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, MoreHorizontal, Search } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import { DataTable, EmptyState, TableColumn } from '../../components/common';
import { useDebounce } from '../../hooks/useDebounce';
import authService from '../../auth/authService';
import machineService from '../../services/machineService';
import machineMaintenanceService from '../../services/machineMaintenanceService';
import {
  computeMaintenanceSummary,
  enrichMaintenanceWindowsWithMachines,
  getMaintenanceStatusLabel,
  mapMaintenanceToTableRow,
} from '../../utils/maintenanceMapper';
import type { Machine } from '../../types/machine.types';
import type {
  MaintenanceColumnKey,
  MaintenanceStatus,
  MaintenanceTableRow,
  MaintenanceTimeframe,
  MaintenanceWindow,
} from '../../types/maintenance.types';
import MachineMaintenanceDetailPanel from './MachineMaintenanceDetailPanel';
import ScheduleMaintenanceDialog from './ScheduleMaintenanceDialog';
import './MachineManagement.css';

const COLUMN_LABELS: Record<MaintenanceColumnKey, string> = {
  machine: 'Machine',
  scheduledStart: 'Scheduled start',
  scheduledEnd: 'Scheduled end',
  reason: 'Reason',
  status: 'Status',
  actions: 'Actions',
};

const DEFAULT_VISIBLE_COLUMNS: Record<MaintenanceColumnKey, boolean> = {
  machine: true,
  scheduledStart: true,
  scheduledEnd: true,
  reason: true,
  status: true,
  actions: true,
};

const STATUS_FILTER_OPTIONS: { value: MaintenanceStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TIMEFRAME_OPTIONS: { value: MaintenanceTimeframe; label: string }[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'active', label: 'Active now' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All time' },
];

export interface MachineMaintenanceTabProps {
  machines: Machine[];
  onNotify: (opts: { title: string; message: string; type: 'success' | 'error' | 'info' }) => void;
  /** Increment to open the schedule dialog from the parent topbar CTA. */
  scheduleOpenSignal?: number;
}

function StatusCell({ row }: Readonly<{ row: MaintenanceTableRow }>) {
  return (
    <div className="mm-status-cell">
      <span className={`mm-status-badge mm-status-badge--${row.status}`}>
        {getMaintenanceStatusLabel(row.status)}
      </span>
      {row.mwlBlocked ? <span className="mm-mwl-badge">MWL blocked</span> : null}
      {row.timeRemainingLabel ? (
        <span className="mm-time-left">{row.timeRemainingLabel}</span>
      ) : null}
    </div>
  );
}

function incrementScheduleDialogKey(key: number): number {
  return key + 1;
}

function findMaintenanceWindowById(
  windows: MaintenanceWindow[],
  id: string
): MaintenanceWindow | undefined {
  for (const item of windows) {
    if (item.id === id) {
      return item;
    }
  }
  return undefined;
}

function MaintenanceSummaryCards({
  summary,
}: Readonly<{
  summary: ReturnType<typeof computeMaintenanceSummary>;
}>) {
  return (
    <div
      className="mm-summary-cards"
      aria-label="Maintenance summary"
    >
      <div className="mm-summary-card mm-summary-card--active">
        <div className="mm-summary-value">{summary.activeNow}</div>
        <div className="mm-summary-title">Active now</div>
        <div className="mm-summary-sub">MWL blocked</div>
      </div>
      <div className="mm-summary-card mm-summary-card--upcoming">
        <div className="mm-summary-value">{summary.upcoming7d}</div>
        <div className="mm-summary-title">Upcoming (7d)</div>
        <div className="mm-summary-sub">Scheduled</div>
      </div>
      <div className="mm-summary-card mm-summary-card--completed">
        <div className="mm-summary-value">{summary.completedThisMonth}</div>
        <div className="mm-summary-title">Completed</div>
        <div className="mm-summary-sub">This month</div>
      </div>
      <div className="mm-summary-card mm-summary-card--cancelled">
        <div className="mm-summary-value">{summary.cancelledAllTime}</div>
        <div className="mm-summary-title">Cancelled</div>
        <div className="mm-summary-sub">All time</div>
      </div>
    </div>
  );
}

function MaintenanceToolbar({
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  timeframe,
  setTimeframe,
  machineFilter,
  setMachineFilter,
  machineFilterOptions,
  columnsRef,
  columnsOpen,
  setColumnsOpen,
  toggleableColumns,
  columnVisibility,
  setColumnVisibility,
}: Readonly<{
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  statusFilter: MaintenanceStatus | 'all';
  setStatusFilter: (v: MaintenanceStatus | 'all') => void;
  timeframe: MaintenanceTimeframe;
  setTimeframe: (v: MaintenanceTimeframe) => void;
  machineFilter: string;
  setMachineFilter: (v: string) => void;
  machineFilterOptions: { value: string; label: string }[];
  columnsRef: React.RefObject<HTMLDivElement>;
  columnsOpen: boolean;
  setColumnsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleableColumns: MaintenanceColumnKey[];
  columnVisibility: Record<MaintenanceColumnKey, boolean>;
  setColumnVisibility: React.Dispatch<React.SetStateAction<Record<MaintenanceColumnKey, boolean>>>;
}>) {
  return (
    <div className="machine-management-toolbar mm-toolbar">
      <div className="machine-management-search-wrap">
        <Search
          className="machine-management-search-icon"
          size={18}
          strokeWidth={ICON_STROKE_WIDTH}
        />
        <input
          type="search"
          className="machine-management-search"
          placeholder="Search by machine, modality, location…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          aria-label="Search maintenance"
        />
      </div>

      <select
        className="mm-filter-select"
        value={statusFilter}
        onChange={e => setStatusFilter(e.target.value as MaintenanceStatus | 'all')}
        aria-label="Filter by status"
      >
        {STATUS_FILTER_OPTIONS.map(opt => (
          <option
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>

      <select
        className="mm-filter-select"
        value={timeframe}
        onChange={e => setTimeframe(e.target.value as MaintenanceTimeframe)}
        aria-label="Filter by timeframe"
      >
        {TIMEFRAME_OPTIONS.map(opt => (
          <option
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>

      <select
        className="mm-filter-select"
        value={machineFilter}
        onChange={e => setMachineFilter(e.target.value)}
        aria-label="Filter by machine"
      >
        {machineFilterOptions.map(opt => (
          <option
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>

      <div
        className="machine-management-columns-wrap"
        ref={columnsRef}
      >
        <button
          type="button"
          className="machine-management-columns-btn"
          onClick={() => setColumnsOpen(prev => !prev)}
          aria-expanded={columnsOpen}
        >
          Columns
          <ChevronDown
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </button>
        {columnsOpen ? (
          <div
            className="machine-management-columns-menu"
            role="menu"
          >
            {toggleableColumns.map(key => (
              <label
                key={key}
                className="machine-management-columns-item"
              >
                <input
                  type="checkbox"
                  checked={columnVisibility[key]}
                  onChange={() =>
                    setColumnVisibility(prev => ({
                      ...prev,
                      [key]: !prev[key],
                    }))
                  }
                />
                {COLUMN_LABELS[key]}
              </label>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MaintenanceTableBody({
  loading,
  error,
  tableRows,
  hasActiveFilters,
  openSchedule,
  columns,
  handleRowClick,
}: Readonly<{
  loading: boolean;
  error: string | null;
  tableRows: MaintenanceTableRow[];
  hasActiveFilters: boolean;
  openSchedule: () => void;
  columns: TableColumn<MaintenanceTableRow>[];
  handleRowClick: (row: MaintenanceTableRow) => void;
}>) {
  if (loading) {
    return <p className="machine-management-empty">Loading maintenance…</p>;
  }
  if (error) {
    return <p className="machine-management-empty">{error}</p>;
  }
  if (tableRows.length === 0) {
    return (
      <div className="machine-management-empty-wrap">
        <EmptyState
          title="No maintenance windows"
          description={
            hasActiveFilters
              ? 'No maintenance windows match your filters.'
              : 'Schedule a maintenance window to block MWL for a machine.'
          }
          actionLabel={hasActiveFilters ? undefined : 'Schedule maintenance'}
          onAction={hasActiveFilters ? undefined : openSchedule}
        />
      </div>
    );
  }
  return (
    <DataTable
      columns={columns as TableColumn[]}
      data={tableRows as unknown as Record<string, unknown>[]}
      onRowClick={row => handleRowClick(row as unknown as MaintenanceTableRow)}
      getRowKey={(row, index) => (row as unknown as MaintenanceTableRow).id ?? index}
      pageScrollStickyHeader
      fullWidth
    />
  );
}

const MachineMaintenanceTab: React.FC<MachineMaintenanceTabProps> = ({
  machines: machinesFromParent,
  onNotify,
  scheduleOpenSignal = 0,
}) => {
  const facility = authService.getFacility();
  const facilityName = facility?.facility_name || 'GIMS NOIDA';

  const [pickerMachines, setPickerMachines] = useState<Machine[]>(machinesFromParent);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | 'all'>('all');
  const [timeframe, setTimeframe] = useState<MaintenanceTimeframe>('upcoming');
  const [machineFilter, setMachineFilter] = useState<string>('all');
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [allWindowsForSummary, setAllWindowsForSummary] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingWindow, setEditingWindow] = useState<MaintenanceWindow | null>(null);
  const [scheduleDialogKey, setScheduleDialogKey] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const [columnVisibility, setColumnVisibility] =
    useState<Record<MaintenanceColumnKey, boolean>>(DEFAULT_VISIBLE_COLUMNS);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [rowMenuId, setRowMenuId] = useState<string | null>(null);
  const columnsRef = useRef<HTMLDivElement>(null);
  const rowMenuRef = useRef<HTMLDivElement>(null);
  const lastScheduleSignal = useRef(scheduleOpenSignal);

  useEffect(() => {
    setPickerMachines(prev => (prev.length ? prev : machinesFromParent));
  }, [machinesFromParent]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await machineService.getAllMachines({ offset: 0, limit: 200 });
      if (!cancelled && result.success && result.data?.machines?.length) {
        setPickerMachines(result.data.machines);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchWindows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [filtered, all] = await Promise.all([
        machineMaintenanceService.listWindows(
          {
            search: debouncedSearch || undefined,
            status: statusFilter,
            timeframe,
            machineId: machineFilter,
          },
          facilityName
        ),
        machineMaintenanceService.listWindows({ timeframe: 'all', status: 'all' }, facilityName),
      ]);

      if (filtered.success && filtered.data) {
        setWindows(filtered.data);
      } else {
        setWindows([]);
        setError(filtered.error || 'Failed to load maintenance windows.');
      }

      if (all.success && all.data) {
        setAllWindowsForSummary(all.data);
      }
    } catch {
      setWindows([]);
      setError('Failed to load maintenance windows.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, timeframe, machineFilter, facilityName]);

  useEffect(() => {
    fetchWindows();
  }, [fetchWindows]);

  useEffect(() => {
    if (scheduleOpenSignal > 0 && scheduleOpenSignal !== lastScheduleSignal.current) {
      lastScheduleSignal.current = scheduleOpenSignal;
      setEditingWindow(null);
      setScheduleDialogKey(key => key + 1);
      setScheduleOpen(true);
    }
  }, [scheduleOpenSignal]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (columnsRef.current && !columnsRef.current.contains(target)) {
        setColumnsOpen(false);
      }
      if (rowMenuRef.current && !rowMenuRef.current.contains(target)) {
        setRowMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const enrichedWindows = useMemo(
    () => enrichMaintenanceWindowsWithMachines(windows, pickerMachines),
    [windows, pickerMachines]
  );

  const enrichedAllWindowsForSummary = useMemo(
    () => enrichMaintenanceWindowsWithMachines(allWindowsForSummary, pickerMachines),
    [allWindowsForSummary, pickerMachines]
  );

  const summary = useMemo(
    () => computeMaintenanceSummary(enrichedAllWindowsForSummary),
    [enrichedAllWindowsForSummary]
  );

  const selectedWindow = useMemo(
    () =>
      enrichedWindows.find(w => w.id === selectedId) ??
      enrichedAllWindowsForSummary.find(w => w.id === selectedId) ??
      null,
    [enrichedWindows, enrichedAllWindowsForSummary, selectedId]
  );

  const tableRows = useMemo(
    () => enrichedWindows.map(w => mapMaintenanceToTableRow(w)),
    [enrichedWindows]
  );

  const machineFilterOptions = useMemo(() => {
    const names = new Map<string, string>();
    enrichedAllWindowsForSummary.forEach(w => {
      const key = w.machineId != null ? String(w.machineId) : w.machineName;
      names.set(key, w.machineName);
    });
    pickerMachines.forEach(m => {
      names.set(String(m.id), m.machineName);
    });
    return [
      { value: 'all', label: 'All machines' },
      ...Array.from(names.entries()).map(([value, label]) => ({ value, label })),
    ];
  }, [enrichedAllWindowsForSummary, pickerMachines]);

  const openSchedule = useCallback(() => {
    setEditingWindow(null);
    setScheduleDialogKey(incrementScheduleDialogKey);
    setScheduleOpen(true);
  }, []);

  const columns: TableColumn<MaintenanceTableRow>[] = useMemo(() => {
    const cols: TableColumn<MaintenanceTableRow>[] = [];

    if (columnVisibility.machine) {
      cols.push({
        key: 'machineName',
        label: COLUMN_LABELS.machine,
        width: 200,
        flex: 2,
        align: 'left',
        render: (_value, row) => (
          <div>
            <div className="machine-cell-primary">{row.machineName}</div>
            {row.machineSubtitle ? (
              <div className="machine-cell-secondary">{row.machineSubtitle}</div>
            ) : null}
          </div>
        ),
      });
    }
    if (columnVisibility.scheduledStart) {
      cols.push({
        key: 'scheduledStart',
        label: COLUMN_LABELS.scheduledStart,
        width: 160,
        flex: 1.2,
        align: 'left',
      });
    }
    if (columnVisibility.scheduledEnd) {
      cols.push({
        key: 'scheduledEnd',
        label: COLUMN_LABELS.scheduledEnd,
        width: 160,
        flex: 1.2,
        align: 'left',
      });
    }
    if (columnVisibility.reason) {
      cols.push({
        key: 'reason',
        label: COLUMN_LABELS.reason,
        width: 180,
        flex: 1.5,
        align: 'left',
        render: (_value, row) => (
          <span
            className="mm-reason-cell"
            title={row.reason}
          >
            {row.reason}
          </span>
        ),
      });
    }
    if (columnVisibility.status) {
      cols.push({
        key: 'status',
        label: COLUMN_LABELS.status,
        width: 200,
        flex: 1.5,
        align: 'left',
        render: (_value, row) => <StatusCell row={row} />,
      });
    }
    if (columnVisibility.actions) {
      cols.push({
        key: 'actions',
        label: COLUMN_LABELS.actions,
        width: 72,
        flex: 0,
        align: 'center',
        render: (_value, row) => (
          <div
            className="mm-row-actions"
            ref={rowMenuId === row.id ? rowMenuRef : undefined}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="mm-row-menu-btn"
              aria-label="Row actions"
              aria-expanded={rowMenuId === row.id}
              onClick={() => setRowMenuId(rowMenuId === row.id ? null : row.id)}
            >
              <MoreHorizontal
                size={18}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
            {rowMenuId === row.id ? (
              <div
                className="mm-row-menu"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setSelectedId(row.id);
                    setRowMenuId(null);
                  }}
                >
                  View details
                </button>
                {(row.status === 'scheduled' || row.status === 'in_progress') && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      const w = findMaintenanceWindowById(enrichedWindows, row.id);
                      if (w) {
                        setEditingWindow(w);
                        setScheduleDialogKey(incrementScheduleDialogKey);
                        setScheduleOpen(true);
                      }
                      setRowMenuId(null);
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [columnVisibility, rowMenuId, enrichedWindows]);

  const handleRowClick = useCallback((row: MaintenanceTableRow) => {
    setSelectedId(row.id);
  }, []);

  const handleScheduleSuccess = useCallback(
    (window: MaintenanceWindow) => {
      onNotify({
        title: editingWindow ? 'Maintenance updated' : 'Maintenance scheduled',
        message: editingWindow
          ? `${window.machineName} maintenance window was updated.`
          : `Maintenance window scheduled for ${window.machineName}.`,
        type: 'success',
      });
      setEditingWindow(null);
      setSelectedId(window.id);
      fetchWindows();
    },
    [editingWindow, fetchWindows, onNotify]
  );

  const handleCancelWindow = useCallback(async () => {
    if (!selectedWindow) {
      return;
    }
    setCancelling(true);
    try {
      const result = await machineMaintenanceService.cancelWindow(
        selectedWindow.id,
        selectedWindow.machineId,
        facilityName
      );
      if (result.success && result.data) {
        onNotify({
          title: 'Window cancelled',
          message: `Maintenance for ${result.data.machineName} was cancelled.`,
          type: 'success',
        });
        setSelectedId(result.data.id);
        fetchWindows();
      } else {
        onNotify({
          title: 'Cancel failed',
          message: result.error || 'Could not cancel maintenance window.',
          type: 'error',
        });
      }
    } catch {
      onNotify({
        title: 'Cancel failed',
        message: 'Could not cancel maintenance window.',
        type: 'error',
      });
    } finally {
      setCancelling(false);
    }
  }, [selectedWindow, fetchWindows, onNotify, facilityName]);

  const toggleableColumns = useMemo(() => Object.keys(COLUMN_LABELS) as MaintenanceColumnKey[], []);

  const hasActiveFilters =
    Boolean(debouncedSearch) || statusFilter !== 'all' || machineFilter !== 'all';

  return (
    <div className="machine-management-body machine-maintenance-body">
      <div
        className={`machine-management-list-pane ${
          selectedWindow ? 'machine-management-list-pane--with-panel' : ''
        }`}
      >
        <MaintenanceSummaryCards summary={summary} />

        <MaintenanceToolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          timeframe={timeframe}
          setTimeframe={setTimeframe}
          machineFilter={machineFilter}
          setMachineFilter={setMachineFilter}
          machineFilterOptions={machineFilterOptions}
          columnsRef={columnsRef}
          columnsOpen={columnsOpen}
          setColumnsOpen={setColumnsOpen}
          toggleableColumns={toggleableColumns}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
        />

        <div className="machine-management-table-wrap">
          <MaintenanceTableBody
            loading={loading}
            error={error}
            tableRows={tableRows}
            hasActiveFilters={hasActiveFilters}
            openSchedule={openSchedule}
            columns={columns}
            handleRowClick={handleRowClick}
          />
        </div>
      </div>

      {selectedWindow ? (
        <MachineMaintenanceDetailPanel
          window={selectedWindow}
          onClose={() => setSelectedId(null)}
          onEdit={() => {
            setEditingWindow(selectedWindow);
            setScheduleDialogKey(key => key + 1);
            setScheduleOpen(true);
          }}
          onCancelWindow={handleCancelWindow}
          cancelling={cancelling}
        />
      ) : null}

      <ScheduleMaintenanceDialog
        key={scheduleDialogKey}
        open={scheduleOpen}
        onClose={() => {
          setScheduleOpen(false);
          setEditingWindow(null);
        }}
        onSuccess={handleScheduleSuccess}
        machines={pickerMachines}
        facilityName={facilityName}
        editingWindow={editingWindow}
      />
    </div>
  );
};

export default MachineMaintenanceTab;
